import { AtomEffect, DefaultValue, atom, selector } from 'recoil';

import { ConsumptionUnitEnum } from '../../enum/ConsumptionUnitEnum';
import { OVERVIEW_TABS } from '../../enum/OverviewTabsEnums';
import { PeriodEnum } from '../../enum/PeriodEnum';
import { SdpResponse } from '../../models/SdpResponse';
import { SeriesFilterEnum } from '../../enum/SeriesFilterEnum';
import { migrations } from './migrations';
import { roleAccessState } from '../roleAccessAtom';
import { sdpState } from '../sdpAtom';

export interface UserPreferencesStateProps {
  periodGranularity: PeriodEnum;
  unit: ConsumptionUnitEnum;
  selectedSdpId?: string;
  selectedOverviewMeterTab: string;
  shouldShowByDefaultEnergyGuides: boolean;
  seriesVisibility: {
    type: SeriesFilterEnum;
    label: string;
    isTitle?: boolean;
    isEnable?: boolean;
    isAvailable?: boolean;
    series: number[];
    isHidden?: boolean;
  }[];
  consumptionDateFiler: string | null;
}
type PersistedState = {
  version: number;
  data: UserPreferencesStateProps;
};
const STORAGE_VERSION = 1;

function migrate(persisted: unknown): UserPreferencesStateProps {
  const persistedState = (persisted as Partial<PersistedState>) ?? {};
  const version = persistedState.version ?? 0;
  let data = (persistedState.data ?? persisted) as UserPreferencesStateProps;

  let currentVersion = version;

  while (currentVersion < STORAGE_VERSION) {
    const migration = migrations[currentVersion];

    if (!migration) {
      throw new Error(`Missing migration ${currentVersion}`);
    }

    data = migration(data);
    currentVersion++;
  }

  return data;
}
const localStorageEffect =
  (key: string): AtomEffect<UserPreferencesStateProps> =>
  ({ setSelf, onSet }) => {
    const savedValue = localStorage.getItem(key);
    if (savedValue != null) {
      try {
        const persisted: PersistedState = JSON.parse(savedValue);
        const migrated = migrate(persisted);
        setSelf(migrated);

        localStorage.setItem(
          key,
          JSON.stringify({
            version: STORAGE_VERSION,
            data: migrated,
          })
        );
      } catch {
        localStorage.removeItem(key);
      }
    }

    onSet(
      (
        newValue: UserPreferencesStateProps,
        _oldValue: UserPreferencesStateProps | DefaultValue,
        isReset: boolean
      ) => {
        isReset
          ? localStorage.removeItem(key)
          : localStorage.setItem(
              key,
              JSON.stringify({
                version: STORAGE_VERSION,
                data: newValue,
              })
            );
      }
    );
  };

export const userPreferencesSelectedSdpState = selector<SdpResponse | undefined>({
  key: 'userPreferencesSelectedSdp',
  get: ({ get }) => {
    const userPreferences = get(userPreferencesState);
    const sdpId = userPreferences.selectedSdpId;
    if (!sdpId) return undefined;
    const sdp = get(sdpState);
    return sdp?.items?.find((s) => s.id === sdpId);
  },
});

export const userPreferenceUnitState = selector<ConsumptionUnitEnum>({
  key: 'userPreferenceUnit',
  get: ({ get }) => {
    const roleAccess = get(roleAccessState);
    return roleAccess?.myConsumption.hasToggleEuro && roleAccess.forecast.hasToggleEuro
      ? get(userPreferencesState)?.unit
      : ConsumptionUnitEnum.KWH;
  },
});

export const defaultSeriesVisibility = [
  {
    type: SeriesFilterEnum.PREV_TOTAL_CONSUMPTION,
    isTitle: false,
    isEnable: true,
    isHidden: true,
    label: 'overview.meter.detail.consumption.filters.previous.total.consumption',
    series: [0],
  },
  {
    type: SeriesFilterEnum.TOTAL_CONSUMPTION,
    isTitle: true,
    isEnable: true,
    label: 'overview.meter.detail.consumption.filters.total.consumption',
    series: [1],
  },
  {
    type: SeriesFilterEnum.DAY_NIGHT,
    label: 'overview.meter.detail.consumption.filters.day.night',
    series: [4, 5],
  },
  {
    type: SeriesFilterEnum.LOCAL_NETWORK_CONSUMPTION,
    label: 'overview.meter.detail.consumption.filters.local.shared',
    series: [6, 7],
  },
  {
    type: SeriesFilterEnum.PREV_TOTAL_INJECTION,
    isTitle: false,
    isEnable: true,
    isHidden: true,
    label: 'overview.meter.detail.consumption.filters.previous.total.injection',
    series: [2],
  },
  {
    type: SeriesFilterEnum.TOTAL_INJECTION,
    isTitle: true,
    isEnable: true,
    label: 'overview.meter.detail.consumption.filters.total.injection',
    series: [3],
  },

  {
    type: SeriesFilterEnum.LOCAL_NETWORK_INJECTION,
    label: 'overview.meter.detail.consumption.filters.injection.local.shared',
    series: [8, 9],
  },
];
export const userPreferencesState = atom<UserPreferencesStateProps>({
  key: 'userPreferencesState',
  default: {
    periodGranularity: PeriodEnum.DAILY,
    unit: ConsumptionUnitEnum.KWH,
    selectedOverviewMeterTab: OVERVIEW_TABS.CONSUMPTION,
    consumptionDateFiler: null,
    seriesVisibility: defaultSeriesVisibility,
    shouldShowByDefaultEnergyGuides: true,
  },
  effects: [localStorageEffect('user_preferences_state')],
});
